import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-hot-toast";
import { FaArrowLeft, FaSave, FaPlus, FaTrash, FaSpinner, FaArrowRight, FaCheck, FaEdit, 
         FaTimes, FaImage, FaFileExcel, FaUpload, FaDownload, FaSync, FaBars } from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";
import { useGetDepartmentsQuery } from "../slices/departmentsApiSlice";
import { useGetSubjectsQuery } from "../slices/subjectsApiSlice";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import config from "../config/config";
import Loader from "../components/Loader";

const PaperCreationScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(id ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1); // 1: Paper Details, 2: Questions
  
  // Form state
  const [paperData, setPaperData] = useState({
    title: "",
    description: "",
    department: "",
    subject: "",
    questions: []
  });

  // State for directly fetched departments and subjects
  const [directDepartments, setDirectDepartments] = useState([]);
  const [directSubjects, setDirectSubjects] = useState([]);
  const [isDirectFetching, setIsDirectFetching] = useState(false);
  const [isDirectFetchingSubjects, setIsDirectFetchingSubjects] = useState(false);

  // Current question being edited
  const [currentQuestion, setCurrentQuestion] = useState({
    text: "",
    image: null,
    imagePreview: null,
    questionType: "single", // 'single' or 'multiple'
    options: [
      { text: "", image: null, imagePreview: null, isCorrect: false },
      { text: "", image: null, imagePreview: null, isCorrect: false },
      { text: "", image: null, imagePreview: null, isCorrect: false },
      { text: "", image: null, imagePreview: null, isCorrect: false }
    ],
    correctOption: 0, // For single choice questions
    correctOptions: [] // For multiple choice questions
  });

  // Editing state
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingQuestionImage, setIsUploadingQuestionImage] = useState(false);
  const [currentUploadingOptionIndex, setCurrentUploadingOptionIndex] = useState(null);
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  
  // Excel file state
  const [excelFile, setExcelFile] = useState(null);
  
  // File input refs
  const questionImageRef = useRef(null);
  const fileInputRef = useRef(null);
  const optionImageRefs = useRef([]);

  // Get departments and subjects from API
  const { data: departments, isLoading: loadingDepartments, error: departmentsError } = useGetDepartmentsQuery();
  const { data: subjects, isLoading: loadingSubjects, error: subjectsError } = useGetSubjectsQuery(selectedDepartment);
  
  // Log department data for debugging
  useEffect(() => {
    console.log('Departments data:', departments);
    console.log('Loading departments:', loadingDepartments);
    console.log('Departments error:', departmentsError);
    
    // If there's an error or no departments loaded, try direct fetch as fallback
    if ((departmentsError || (!departments && !loadingDepartments)) && !isDirectFetching && directDepartments.length === 0) {
      fetchDepartmentsDirect();
    }
  }, [departments, loadingDepartments, departmentsError]);

  // Log subject data for debugging
  useEffect(() => {
    console.log('Subjects data:', subjects);
    console.log('Loading subjects:', loadingSubjects);
    console.log('Subjects error:', subjectsError);
    console.log('Selected department:', selectedDepartment);
    
    // If there's a selected department and subjects aren't loading, try direct fetch
    if (selectedDepartment && 
        (subjectsError || (!subjects && !loadingSubjects)) && 
        !isDirectFetchingSubjects) {
      fetchSubjectsDirect(selectedDepartment);
    }
  }, [subjects, loadingSubjects, subjectsError, selectedDepartment]);

  // Fallback function to fetch departments directly
  const fetchDepartmentsDirect = async () => {
    try {
      setIsDirectFetching(true);
      const response = await axiosInstance.get('/api/departments');
      
      if (response.data && Array.isArray(response.data)) {
        console.log('Direct fetch departments success:', response.data);
        setDirectDepartments(response.data);
      }
    } catch (error) {
      console.error('Direct fetch departments error:', error);
      toast.error('Failed to load departments. Please try again.');
    } finally {
      setIsDirectFetching(false);
    }
  };

  // Fallback function to fetch subjects directly
  const fetchSubjectsDirect = async (departmentId) => {
    if (!departmentId) return;
    
    try {
      setIsDirectFetchingSubjects(true);
      const response = await axiosInstance.get(`/api/subjects?department=${departmentId}`);
      
      if (response.data && Array.isArray(response.data)) {
        console.log('Direct fetch subjects success:', response.data);
        setDirectSubjects(response.data);
      }
    } catch (error) {
      console.error('Direct fetch subjects error:', error);
    } finally {
      setIsDirectFetchingSubjects(false);
    }
  };

  // Use departments from RTK Query or fallback to direct fetch
  const departmentsToUse = departments?.length > 0 ? departments : directDepartments;
  const isLoadingDepartments = loadingDepartments || isDirectFetching;
  
  // Use subjects from RTK Query or fallback to direct fetch
  const subjectsToUse = subjects?.length > 0 ? subjects : directSubjects;
  const isLoadingSubjectsActual = loadingSubjects || isDirectFetchingSubjects;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { when: "afterChildren" }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 }
  };

  // Create an empty question
  const createEmptyQuestion = () => {
    return {
      text: "",
      image: null,
      imagePreview: null,
      questionType: "single", // Default to single choice
      options: [
        { text: "", image: null, imagePreview: null, isCorrect: false },
        { text: "", image: null, imagePreview: null, isCorrect: false },
        { text: "", image: null, imagePreview: null, isCorrect: false },
        { text: "", image: null, imagePreview: null, isCorrect: false }
      ],
      correctOption: 0,
      correctOptions: []
    };
  };

  // Initialize with one empty question if creating a new paper
  useEffect(() => {
    if (!id && paperData.questions.length === 0) {
      setPaperData(prev => ({
        ...prev,
        questions: [createEmptyQuestion()]
      }));
    }
  }, [id]);

  // Fetch paper data if editing
  useEffect(() => {
    if (id) {
      const fetchPaperData = async () => {
        try {
          setLoading(true);
          const { data } = await axiosInstance.get(`/api/papers/${id}`);
          
          // Format the questions correctly
          const formattedQuestions = data.questions.map(q => {
            return {
              ...q,
              questionType: q.correctOptions?.length > 1 ? "multiple" : "single",
              imagePreview: q.image ? `${config.backendUrl}/${q.image}` : null,
              options: q.options.map(opt => ({
                ...opt,
                imagePreview: opt.image ? `${config.backendUrl}/${opt.image}` : null
              }))
            };
          });
          
          setPaperData({
            title: data.title,
            description: data.description,
            department: data.department?._id || data.department,
            subject: data.subject?._id || data.subject,
            questions: formattedQuestions
          });
          
          setSelectedDepartment(data.department?._id || data.department);
          setCurrentStep(1);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching paper:', error);
          toast.error('Failed to load paper data');
          setLoading(false);
          navigate('/faculty-dashboard');
        }
      };

      fetchPaperData();
    }
  }, [id, navigate]);

  // Handle paper details changes
  const handlePaperChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'department') {
      setSelectedDepartment(value);
      setPaperData({
        ...paperData,
        department: value,
        subject: "" // Reset subject when department changes
      });
    } else {
      setPaperData({
        ...paperData,
        [name]: value
      });
    }
  };

  // Handle question changes
  const handleQuestionChange = (e) => {
    const { name, value } = e.target;
    setCurrentQuestion({
      ...currentQuestion,
      [name]: value
    });
  };

  // Handle option text changes
  const handleOptionChange = (index, e) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      text: e.target.value
    };
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions
    });
  };

  // Handle correct option selection for single choice questions
  const handleCorrectOptionChange = (index) => {
    if (currentQuestion.questionType === "single") {
      const updatedOptions = currentQuestion.options.map((option, i) => ({
        ...option,
        isCorrect: i === index
      }));
      
      setCurrentQuestion({
        ...currentQuestion,
        options: updatedOptions,
        correctOption: index
      });
    } else {
      // For multiple choice
      const updatedOptions = [...currentQuestion.options];
      updatedOptions[index] = {
        ...updatedOptions[index],
        isCorrect: !updatedOptions[index].isCorrect
      };
      
      const correctOptions = updatedOptions
        .map((opt, i) => opt.isCorrect ? i : null)
        .filter(idx => idx !== null);
      
      setCurrentQuestion({
        ...currentQuestion,
        options: updatedOptions,
        correctOptions
      });
    }
  };

  // Handle question type change
  const handleQuestionTypeChange = (type) => {
    let updatedOptions = [...currentQuestion.options];
    let correctOption = 0;
    let correctOptions = [];
    
    if (type === "single") {
      // Convert to single choice
      updatedOptions = updatedOptions.map((opt, idx) => ({
        ...opt,
        isCorrect: idx === correctOption
      }));
    } else {
      // Convert to multiple choice - by default, keep any that were correct before
      correctOptions = updatedOptions
        .map((opt, i) => opt.isCorrect ? i : null)
        .filter(idx => idx !== null);
      
      if (correctOptions.length === 0) {
        // If none were correct, set the first one as correct
        updatedOptions[0].isCorrect = true;
        correctOptions = [0];
      }
    }
    
    setCurrentQuestion({
      ...currentQuestion,
      questionType: type,
      options: updatedOptions,
      correctOption,
      correctOptions
    });
  };

  // Add a new question
  const handleAddQuestion = () => {
    const questionValidation = validateQuestion();
    if (!questionValidation.isValid) {
      toast.error(questionValidation.message);
      return;
    }
    
    // Format current question for saving
    const formattedQuestion = {
      ...currentQuestion,
      correctOptions: currentQuestion.questionType === "single" 
        ? [currentQuestion.correctOption] 
        : currentQuestion.correctOptions
    };
    
    setPaperData({
      ...paperData,
      questions: [...paperData.questions, formattedQuestion]
    });
    
    // Reset the current question
    setCurrentQuestion(createEmptyQuestion());
    toast.success('Question added successfully');
  };

  // Edit a question
  const handleEditQuestion = (index) => {
    setEditingQuestionIndex(index);
    setCurrentQuestion(paperData.questions[index]);
    setIsEditModalOpen(true);
  };

  // Save edited question
  const handleSaveEditedQuestion = () => {
    const questionValidation = validateQuestion();
    if (!questionValidation.isValid) {
      toast.error(questionValidation.message);
      return;
    }
    
    // Format current question for saving
    const formattedQuestion = {
      ...currentQuestion,
      correctOptions: currentQuestion.questionType === "single" 
        ? [currentQuestion.correctOption] 
        : currentQuestion.correctOptions
    };
    
    const updatedQuestions = [...paperData.questions];
    updatedQuestions[editingQuestionIndex] = formattedQuestion;
    
    setPaperData({
      ...paperData,
      questions: updatedQuestions
    });
    
    // Reset editing state
    setEditingQuestionIndex(null);
    setCurrentQuestion(createEmptyQuestion());
    setIsEditModalOpen(false);
    toast.success('Question updated successfully');
  };

  // Delete a question
  const handleDeleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      const updatedQuestions = [...paperData.questions];
      updatedQuestions.splice(index, 1);
      
      setPaperData({
        ...paperData,
        questions: updatedQuestions
      });
      
      toast.success('Question deleted successfully');
    }
  };
  
  // Upload question image
  const handleQuestionImageClick = () => {
    questionImageRef.current?.click();
  };
  
  const handleQuestionImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF)');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCurrentQuestion({
        ...currentQuestion,
        image: file,
        imagePreview: reader.result
      });
    };
    reader.readAsDataURL(file);
    
    // Upload to server if needed immediately
    if (id) {
      try {
        setIsUploadingQuestionImage(true);
        
        const formData = new FormData();
        formData.append('image', file);
        
        const { data } = await axiosInstance.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        setCurrentQuestion({
          ...currentQuestion,
          image: data.path,
          imagePreview: `${config.backendUrl}/${data.path}`
        });
        
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image');
      } finally {
        setIsUploadingQuestionImage(false);
      }
    }
  };
  
  // Remove question image
  const handleRemoveQuestionImage = () => {
    setCurrentQuestion({
      ...currentQuestion,
      image: null,
      imagePreview: null
    });
    if (questionImageRef.current) {
      questionImageRef.current.value = '';
    }
  };
  
  // Upload option image
  const handleOptionImageClick = (index) => {
    optionImageRefs.current[index]?.click();
  };
  
  const handleOptionImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF)');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const updatedOptions = [...currentQuestion.options];
      updatedOptions[index] = {
        ...updatedOptions[index],
        image: file,
        imagePreview: reader.result
      };
      
      setCurrentQuestion({
        ...currentQuestion,
        options: updatedOptions
      });
    };
    reader.readAsDataURL(file);
    
    // Upload to server if needed immediately
    if (id) {
      try {
        setCurrentUploadingOptionIndex(index);
        
        const formData = new FormData();
        formData.append('image', file);
        
        const { data } = await axiosInstance.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const updatedOptions = [...currentQuestion.options];
        updatedOptions[index] = {
          ...updatedOptions[index],
          image: data.path,
          imagePreview: `${config.backendUrl}/${data.path}`
        };
        
        setCurrentQuestion({
          ...currentQuestion,
          options: updatedOptions
        });
        
        toast.success('Option image uploaded successfully');
      } catch (error) {
        console.error('Error uploading option image:', error);
        toast.error('Failed to upload option image');
      } finally {
        setCurrentUploadingOptionIndex(null);
      }
    }
  };
  
  // Remove option image
  const handleRemoveOptionImage = (index) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      image: null,
      imagePreview: null
    };
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions
    });
    
    if (optionImageRefs.current[index]) {
      optionImageRefs.current[index].value = '';
    }
  };
  
  // Handle Excel file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setExcelFile(null);
      return;
    }
    
    // Check file type
    const fileType = file.name.split('.').pop().toLowerCase();
    if (fileType !== 'xlsx' && fileType !== 'xls') {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setExcelFile(file);
  };
  
  // Handle Excel upload
  const handleExcelUpload = async () => {
    if (!excelFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setIsUploadingExcel(true);
    
    try {
      // Read the Excel file directly without sending to server
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (!jsonData || !Array.isArray(jsonData) || jsonData.length === 0) {
            throw new Error('Invalid Excel format');
          }
          
          // Transform Excel data to match our format
          // Expected columns: "Question Text", "Option A", "Option B", "Option C", "Option D", "Correct Option(s)"
          const transformedQuestions = jsonData.map((row, index) => {
            // Parse the Excel row data
            const questionText = row["Question Text"] || `Question ${index + 1}`;
            
            // Get options data
            const optionTexts = [
              row["Option A"] || "",
              row["Option B"] || "",
              row["Option C"] || "",
              row["Option D"] || ""
            ];
            
            // Parse correct options
            let correctOptions = [];
            const correctOptionStr = row["Correct Option(s)"] || "";
            
            if (correctOptionStr) {
              // Handle both formats: "A,C" or "0,2" or "A" or "0"
              if (correctOptionStr.includes(",")) {
                // Multiple correct options
                const optionLetters = correctOptionStr.split(",").map(o => o.trim());
                correctOptions = optionLetters.map(letter => {
                  // Convert letter (A,B,C,D) to index (0,1,2,3) if needed
                  if (isNaN(letter)) {
                    return letter.charCodeAt(0) - 65; // 'A' -> 0, 'B' -> 1, etc.
                  }
                  return parseInt(letter);
                });
              } else {
                // Single correct option
                if (isNaN(correctOptionStr)) {
                  // If it's a letter (A,B,C,D)
                  correctOptions = [correctOptionStr.charCodeAt(0) - 65];
                } else {
                  // If it's a number
                  correctOptions = [parseInt(correctOptionStr)];
                }
              }
            }
            
            // Ensure correctOptions contains valid indices (0-3)
            correctOptions = correctOptions.filter(i => i >= 0 && i < 4);
            
            // If no valid correct options, default to first option
            if (correctOptions.length === 0) {
              correctOptions = [0];
            }
            
            // Determine question type
            const questionType = correctOptions.length > 1 ? "multiple" : "single";
            
            // Create options with correct flags
            const options = optionTexts.map((text, i) => ({
              text: String(text || ""), // Ensure text is a string
              image: null,
              imagePreview: null,
              isCorrect: correctOptions.includes(i)
            }));
            
            return {
              text: String(questionText || ""), // Ensure text is a string
              image: null,
              imagePreview: null,
              questionType,
              options,
              correctOption: questionType === "single" ? correctOptions[0] : 0,
              correctOptions
            };
          }).filter(q => q.text.trim() !== "");
          
          if (transformedQuestions.length === 0) {
            toast.error('No valid questions found in the Excel file');
            return;
          }
          
          // Add questions to paper data
          setPaperData({
            ...paperData,
            questions: [...paperData.questions, ...transformedQuestions]
          });
          
          toast.success(`${transformedQuestions.length} questions imported successfully`);
        } catch (error) {
          console.error('Error processing Excel file:', error);
          toast.error('Failed to process Excel file. Please check the format.');
        } finally {
          setIsUploadingExcel(false);
          // Reset file input
          setExcelFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      
      reader.onerror = () => {
        toast.error('Failed to read Excel file');
        setIsUploadingExcel(false);
      };
      
      reader.readAsArrayBuffer(excelFile);
    } catch (error) {
      console.error('Error uploading Excel:', error);
      toast.error('Failed to import questions. Please check the Excel format.');
      setIsUploadingExcel(false);
    }
  };
  
  // Download sample Excel format
  const downloadSampleExcel = () => {
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Define headers and sample data
    const headers = ["Question Text", "Option A", "Option B", "Option C", "Option D", "Correct Option(s)"];
    const sampleData = [
      ["What is 2+2?", "3", "4", "5", "6", "B"],
      ["Which are prime numbers?", "2", "4", "5", "9", "A,C"],
      ["Capital of France?", "London", "Berlin", "Paris", "Madrid", "C"]
    ];
    
    // Combine headers and data
    const wsData = [headers, ...sampleData];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    const wscols = [
      { wch: 40 }, // Question Text
      { wch: 20 }, // Option A
      { wch: 20 }, // Option B
      { wch: 20 }, // Option C
      { wch: 20 }, // Option D
      { wch: 15 }  // Correct Option(s)
    ];
    ws['!cols'] = wscols;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, "question_format_sample.xlsx");
  };
  
  // Handle question reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(paperData.questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setPaperData({
      ...paperData,
      questions: items
    });
  };
  
  // Validate question before adding/saving
  const validateQuestion = () => {
    if (!currentQuestion.text.trim()) {
      return { isValid: false, message: 'Question text is required' };
    }
    
    // Check if at least one option has text
    const hasOptionText = currentQuestion.options.some(opt => opt.text.trim());
    if (!hasOptionText) {
      return { isValid: false, message: 'At least one option must have text' };
    }
    
    // For multiple choice, check if at least one option is marked correct
    if (currentQuestion.questionType === "multiple") {
      const hasCorrectOption = currentQuestion.options.some(opt => opt.isCorrect);
      if (!hasCorrectOption) {
        return { isValid: false, message: 'At least one option must be marked as correct' };
      }
    }
    
    return { isValid: true };
  };
  
  // Validate the entire form before submission
  const validateForm = () => {
    if (!paperData.title.trim()) {
      toast.error('Paper title is required');
      return false;
    }
    
    if (!paperData.department) {
      toast.error('Please select a department');
      return false;
    }
    
    if (!paperData.subject) {
      toast.error('Please select a subject');
      return false;
    }
    
    if (paperData.questions.length === 0) {
      toast.error('At least one question is required');
      return false;
    }
    
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      // Process questions and upload images if needed
      const processedQuestions = await Promise.all(
        paperData.questions.map(async (question, qIndex) => {
          // Create a clean copy to ensure only valid data is sent
          const questionCopy = {
            text: typeof question.text === 'string' ? question.text : 'Untitled Question',
            correctOptions: Array.isArray(question.correctOptions) ? question.correctOptions : [],
            questionType: question.questionType === 'multiple' ? 'multiple' : 'single'
          };
          
          // Upload question image if it's a File object
          if (question.image && question.image instanceof File) {
            try {
              const imgFormData = new FormData();
              imgFormData.append('image', question.image);
              
              const { data } = await axiosInstance.post('/api/upload', imgFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });
              
              questionCopy.image = data.path;
            } catch (error) {
              console.error(`Error uploading question ${qIndex + 1} image:`, error);
              toast.error(`Failed to upload image for question ${qIndex + 1}`);
            }
          } else if (question.image) {
            // Pass through existing image path
            questionCopy.image = question.image;
          }
          
          // Process options and upload option images if needed
          if (question.options && Array.isArray(question.options)) {
            questionCopy.options = await Promise.all(
              question.options.map(async (option, oIndex) => {
                // Create clean option
                const optionCopy = {
                  text: typeof option.text === 'string' ? option.text : '',
                  isCorrect: Boolean(option.isCorrect)
                };
                
                if (option.image && option.image instanceof File) {
                  try {
                    const optImgFormData = new FormData();
                    optImgFormData.append('image', option.image);
                    
                    const { data } = await axiosInstance.post('/api/upload', imgFormData, {
                      headers: {
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    
                    optionCopy.image = data.path;
                  } catch (error) {
                    console.error(`Error uploading option ${oIndex + 1} image for question ${qIndex + 1}:`, error);
                    toast.error(`Failed to upload image for option ${oIndex + 1}, question ${qIndex + 1}`);
                  }
                } else if (option.image) {
                  // Pass through existing image path
                  optionCopy.image = option.image;
                }
                
                return optionCopy;
              })
            );
          } else {
            // Create default options if missing
            questionCopy.options = [
              { text: '', isCorrect: true },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false }
            ];
          }
          
          return questionCopy;
        })
      );
      
      // Create clean paper data
      const cleanPaperData = {
        title: paperData.title,
        description: paperData.description || '',
        department: paperData.department,
        subject: paperData.subject,
        questions: processedQuestions
      };
      
      // Submit the form
      if (id) {
        await axiosInstance.put(`/api/papers/${id}`, cleanPaperData);
        toast.success('Paper updated successfully');
      } else {
        await axiosInstance.post('/api/papers', cleanPaperData);
        toast.success('Paper created successfully');
      }
      
      // Navigate back to dashboard
      navigate('/faculty-dashboard');
    } catch (error) {
      console.error('Error submitting paper:', error);
      toast.error('Failed to save paper. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Loader size="large" text="Loading paper content..." />
      </div>
    );
  }

  // Render step indicators
  const renderStepIndicators = () => (
    <div className="flex items-center mb-8">
      <div 
        className={`rounded-full h-10 w-10 flex items-center justify-center font-bold ${
          currentStep === 1 
            ? 'bg-blue-600 text-white'
            : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600')
        }`}
      >
        1
      </div>
      <div 
        className={`h-1 w-12 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}
      />
      <div 
        className={`rounded-full h-10 w-10 flex items-center justify-center font-bold ${
          currentStep === 2 
            ? 'bg-blue-600 text-white'
            : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600')
        }`}
      >
        2
      </div>
      <div className="ml-4 text-sm">
        <span className={currentStep === 1 ? 'font-bold' : ''}>Paper Details</span>
        {' → '}
        <span className={currentStep === 2 ? 'font-bold' : ''}>Questions</span>
      </div>
    </div>
  );

  // Render paper details step
  const renderPaperDetailsStep = () => (
    <motion.div
      key="paper-details"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-6"
    >
      <motion.div 
        variants={itemVariants}
        className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      >
        <h2 className="text-xl font-semibold mb-4">Paper Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div variants={itemVariants}>
            <label htmlFor="title" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Paper Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={paperData.title}
              onChange={handlePaperChange}
              className={`block w-full rounded-md shadow-sm ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Enter paper title"
              required
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <label htmlFor="department" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Department
            </label>
            <select
              id="department"
              name="department"
              value={paperData.department}
              onChange={handlePaperChange}
              className={`block w-full rounded-md shadow-sm ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              required
            >
              <option value="">Select Department</option>
              {isLoadingDepartments ? (
                <option disabled>Loading departments...</option>
              ) : departmentsError && directDepartments.length === 0 ? (
                <option disabled>Error loading departments</option>
              ) : departmentsToUse && departmentsToUse.length > 0 ? (
                departmentsToUse.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))
              ) : (
                <option disabled>No departments available</option>
              )}
            </select>
            {departmentsError && directDepartments.length === 0 && (
              <p className="mt-1 text-sm text-red-600">
                Failed to load departments. Please try refreshing the page.
              </p>
            )}
          </motion.div>
          <motion.div variants={itemVariants}>
            <label htmlFor="subject" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Subject
            </label>
            <select
              id="subject"
              name="subject"
              value={paperData.subject}
              onChange={handlePaperChange}
              className={`block w-full rounded-md shadow-sm ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              disabled={!paperData.department}
              required
            >
              <option value="">
                {!paperData.department 
                  ? "Select a department first" 
                  : isLoadingSubjectsActual 
                    ? "Loading subjects..." 
                    : "Select Subject"}
              </option>
              {!isLoadingSubjectsActual && subjectsToUse && subjectsToUse.length > 0 && subjectsToUse.map((subj) => (
                <option key={subj._id} value={subj._id}>
                  {subj.name}
                </option>
              ))}
              {paperData.department && !isLoadingSubjectsActual && (!subjectsToUse || subjectsToUse.length === 0) && (
                <option disabled>No subjects available for this department</option>
              )}
            </select>
            {subjectsError && directSubjects.length === 0 && paperData.department && (
              <p className="mt-1 text-sm text-red-600">
                Failed to load subjects. Please try refreshing the page.
              </p>
            )}
          </motion.div>
          <motion.div variants={itemVariants}>
            <label htmlFor="description" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Description (Optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={paperData.description}
              onChange={handlePaperChange}
              rows={3}
              className={`block w-full rounded-md shadow-sm ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Brief description of the paper"
            />
          </motion.div>
        </div>
      </motion.div>

      <motion.div 
        variants={itemVariants}
        className="flex justify-end"
      >
        <button
          type="button"
          onClick={() => setCurrentStep(2)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
        >
          Next: Add Questions <FaArrowRight className="ml-2" />
        </button>
      </motion.div>
    </motion.div>
  );

  // Render questions step
  const renderQuestionsStep = () => (
    <motion.div
      key="questions"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-6"
    >
      <motion.div 
        variants={itemVariants}
        className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Questions</h2>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={downloadSampleExcel}
              className={`px-3 py-2 rounded ${
                isDarkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-600 hover:bg-green-700'
              } text-white transition-colors flex items-center text-sm`}
            >
              <FaDownload className="mr-1" /> Sample Excel
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`px-3 py-2 rounded ${
                isDarkMode ? 'bg-indigo-700 hover:bg-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700'
              } text-white transition-colors flex items-center text-sm`}
            >
              <FaFileExcel className="mr-1" /> Import Excel
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center text-sm"
            >
              <FaArrowLeft className="mr-1" /> Back
            </button>
          </div>
        </div>

        {/* Excel Import UI */}
        <motion.div
          variants={itemVariants}
          className={`mb-6 p-4 border rounded-lg ${
            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <h3 className="text-lg font-medium mb-3">Import Questions from Excel</h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <div className="flex">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex items-center px-4 py-2 rounded-l ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-white hover:bg-gray-100'
                  } border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
                >
                  <FaFileExcel className="mr-2 text-green-600" />
                  Select Excel File
                </button>
                <div className={`flex-1 p-2 border-t border-r border-b truncate ${
                  isDarkMode ? 'bg-gray-600 border-gray-600' : 'bg-white border-gray-300'
                } rounded-r`}>
                  {excelFile ? excelFile.name : 'No file selected'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleExcelUpload}
              disabled={!excelFile || isUploadingExcel}
              className={`px-4 py-2 rounded font-medium ${
                !excelFile || isUploadingExcel
                  ? 'bg-gray-400 cursor-not-allowed'
                  : (isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700')
              } text-white transition-colors flex items-center`}
            >
              {isUploadingExcel ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <FaUpload className="mr-2" />
                  Import Questions
                </>
              )}
            </button>
          </div>
          <p className="text-sm mt-2 text-gray-500">
            Upload Excel file with questions. Format: Question, Options A-D, and Correct Option(s)
          </p>
        </motion.div>

        {/* Question Editor */}
        <motion.div
          variants={itemVariants}
          className={`mb-6 p-4 border rounded-lg ${
            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <h3 className="text-lg font-medium mb-3">
            {editingQuestionIndex !== null ? 'Edit Question' : 'Add New Question'}
          </h3>
          
          {/* Question Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Question Type</label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleQuestionTypeChange("single")}
                className={`px-4 py-2 rounded ${
                  currentQuestion.questionType === "single"
                    ? 'bg-blue-600 text-white'
                    : (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                }`}
              >
                Single Choice
              </button>
              <button
                type="button"
                onClick={() => handleQuestionTypeChange("multiple")}
                className={`px-4 py-2 rounded ${
                  currentQuestion.questionType === "multiple"
                    ? 'bg-blue-600 text-white'
                    : (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                }`}
              >
                Multiple Choice
              </button>
            </div>
          </div>
          
          {/* Question Text */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Question Text <span className="text-red-500">*</span>
            </label>
            <textarea
              name="text"
              value={currentQuestion.text}
              onChange={handleQuestionChange}
              rows={3}
              className={`block w-full rounded-md shadow-sm ${
                isDarkMode 
                  ? 'bg-gray-600 border-gray-500 text-white' 
                  : 'bg-white border-gray-300'
              }`}
              placeholder="Enter your question"
            />
          </div>

          {/* Question Image */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Question Image (Optional)</label>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                ref={questionImageRef}
                onChange={handleQuestionImageChange}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={handleQuestionImageClick}
                className={`px-3 py-2 rounded ${
                  isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'
                } flex items-center`}
              >
                {isUploadingQuestionImage ? (
                  <FaSpinner className="animate-spin mr-2" />
                ) : (
                  <FaImage className="mr-2" />
                )}
                {currentQuestion.image ? 'Change Image' : 'Add Image'}
              </button>
              
              {currentQuestion.image && (
                <button
                  type="button"
                  onClick={handleRemoveQuestionImage}
                  className="px-3 py-2 rounded bg-red-500 hover:bg-red-600 text-white flex items-center"
                >
                  <FaTrash className="mr-2" />
                  Remove
                </button>
              )}
            </div>
            
            {currentQuestion.imagePreview && (
              <div className="mt-2 relative">
                <img
                  src={currentQuestion.imagePreview}
                  alt="Question Preview"
                  className="max-h-32 rounded border"
                />
              </div>
            )}
          </div>
          
          {/* Options */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Options ({currentQuestion.questionType === "single" ? "select one correct answer" : "select all correct answers"})
            </label>
            {currentQuestion.options.map((option, optIndex) => (
              <div key={optIndex} className="mb-3">
                <div className="flex items-center mb-2">
                  <div className="mr-2 w-8 text-center">
                    {String.fromCharCode(65 + optIndex)}.
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <input
                        type={currentQuestion.questionType === "single" ? "radio" : "checkbox"}
                        checked={option.isCorrect}
                        onChange={() => handleCorrectOptionChange(optIndex)}
                        className="mr-3 w-5 h-5 accent-blue-600"
                      />
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionChange(optIndex, e)}
                        className={`flex-1 p-2 rounded border ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Option Image */}
                <div className="ml-10 flex items-center space-x-2">
                  <input
                    type="file"
                    ref={(el) => optionImageRefs.current[optIndex] = el}
                    onChange={(e) => handleOptionImageChange(optIndex, e)}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => handleOptionImageClick(optIndex)}
                    className={`px-2 py-1 text-sm rounded ${
                      isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'
                    } flex items-center`}
                  >
                    {currentUploadingOptionIndex === optIndex ? (
                      <FaSpinner className="animate-spin mr-1" />
                    ) : (
                      <FaImage className="mr-1" />
                    )}
                    {option.image ? 'Change Image' : 'Add Image'}
                  </button>
                  
                  {option.image && (
                    <button
                      type="button"
                      onClick={() => handleRemoveOptionImage(optIndex)}
                      className="px-2 py-1 text-sm rounded bg-red-500 hover:bg-red-600 text-white flex items-center"
                    >
                      <FaTrash className="mr-1" />
                      Remove
                    </button>
                  )}
                  
                  {option.imagePreview && (
                    <div className="h-8 w-8 rounded border overflow-hidden">
                      <img
                        src={option.imagePreview}
                        alt={`Option ${String.fromCharCode(65 + optIndex)}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Add/Save Question Button */}
          <div className="flex justify-end">
            {editingQuestionIndex !== null ? (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingQuestionIndex(null);
                    setCurrentQuestion(createEmptyQuestion());
                    setIsEditModalOpen(false);
                  }}
                  className={`px-4 py-2 rounded ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEditedQuestion}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  <FaCheck className="inline mr-2" />
                  Save Changes
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleAddQuestion}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                <FaPlus className="inline mr-2" />
                Add Question
              </button>
            )}
          </div>
        </motion.div>

        {/* Questions List */}
        <motion.div variants={itemVariants}>
          <h3 className="text-lg font-medium mb-3">
            Questions Added ({paperData.questions.length})
          </h3>
          
          {paperData.questions.length === 0 ? (
            <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
              No questions added yet. Use the form above to add questions or import from Excel.
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-4"
                  >
                    {paperData.questions.map((question, index) => (
                      <Draggable key={index} draggableId={`question-${index}`} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`p-4 rounded-lg border ${
                              isDarkMode 
                                ? 'bg-gray-600 border-gray-500' 
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              <div 
                                {...provided.dragHandleProps}
                                className="mr-3 p-1 cursor-grab"
                              >
                                <FaBars className="text-gray-500" />
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between mb-2">
                                  <div className="flex items-center">
                                    <span className="font-medium mr-2">Q{index + 1}.</span>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                      question.questionType === "multiple"
                                        ? 'bg-indigo-100 text-indigo-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {question.questionType === "multiple" 
                                        ? 'Multiple Choice' 
                                        : 'Single Choice'}
                                    </span>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleEditQuestion(index)}
                                      className={`p-1 rounded ${
                                        isDarkMode ? 'hover:bg-gray-500' : 'hover:bg-gray-200'
                                      }`}
                                    >
                                      <FaEdit className="text-blue-600" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteQuestion(index)}
                                      className={`p-1 rounded ${
                                        isDarkMode ? 'hover:bg-gray-500' : 'hover:bg-gray-200'
                                      }`}
                                    >
                                      <FaTrash className="text-red-600" />
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="mb-2">
                                  <div className="font-medium">{typeof question.text === 'string' ? question.text : 'Untitled Question'}</div>
                                  {question.imagePreview && (
                                    <div className="mt-1">
                                      <img
                                        src={question.imagePreview}
                                        alt="Question"
                                        className="max-h-20 rounded"
                                      />
                                    </div>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {question.options && Array.isArray(question.options) && question.options.map((option, optIndex) => (
                                    <div 
                                      key={optIndex}
                                      className={`p-2 rounded flex items-start ${
                                        option.isCorrect
                                          ? (isDarkMode 
                                              ? 'bg-green-800 border-green-700' 
                                              : 'bg-green-100 border-green-200')
                                          : (isDarkMode 
                                              ? 'bg-gray-500 border-gray-400' 
                                              : 'bg-gray-100 border-gray-200')
                                      } border`}
                                    >
                                      <div className={`mr-2 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                        option.isCorrect
                                          ? 'bg-green-600 text-white'
                                          : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')
                                      }`}>
                                        {String.fromCharCode(65 + optIndex)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="truncate">{typeof option.text === 'string' ? option.text : ''}</div>
                                        {option.imagePreview && (
                                          <div className="mt-1">
                                            <img
                                              src={option.imagePreview}
                                              alt={`Option ${String.fromCharCode(65 + optIndex)}`}
                                              className="h-8 rounded"
                                            />
                                          </div>
                                        )}
                                      </div>
                                      {option.isCorrect && (
                                        <div className="ml-1 text-green-600">
                                          <FaCheck />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </motion.div>
      </motion.div>

      <motion.div 
        variants={itemVariants}
        className="flex justify-between"
      >
        <button
          type="button"
          onClick={() => setCurrentStep(1)}
          className={`px-6 py-2 rounded-lg transition-colors inline-flex items-center ${
            isDarkMode 
              ? 'bg-gray-700 text-white hover:bg-gray-600' 
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          <FaArrowLeft className="mr-2" /> Back to Paper Details
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
        >
          {isSaving ? (
            <>
              <FaSpinner className="animate-spin mr-2" /> Saving...
            </>
          ) : (
            <>
              <FaSave className="mr-2" /> Save Paper
            </>
          )}
        </button>
      </motion.div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen pt-24 pb-12 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/faculty-dashboard')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-2xl font-bold">{id ? "Edit Paper" : "Create New Paper"}</h1>
        </div>

        {renderStepIndicators()}
        
        <AnimatePresence mode="wait">
          {currentStep === 1 ? renderPaperDetailsStep() : renderQuestionsStep()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PaperCreationScreen; 